import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import {
  type ExtensionCaptureFailure,
  type ExtensionCapturePayload,
  isExtensionBridgeMessage,
} from '@repo/contracts/extension-bridge';

import { trackEvent } from '../analytics/posthog';
import {
  canvasKeys,
  createCapture,
  startSession,
  uploadCaptureImage,
} from '../api/canvas-captures';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/use-auth';
import { logger } from '../lib/logger';
import { useUiStore } from '../store/ui-store';
import {
  announceReady,
  replyFailure,
  replySuccess,
} from './extension-captures';

const log = logger.child({ module: 'extension-captures' });

/**
 * Captures arriving from the Chrome extension.
 *
 * The extension exists for the pages the Cloud Run browser worker cannot reach
 * — `localhost:3000` above all — and it deliberately holds no credentials: it
 * hands the PNG to this tab, which is already signed in, and this hook puts it
 * through exactly the path `/browse` uses (`upload-url` → PUT to the bucket →
 * `POST /canvas/captures`). Nothing on the API side knows the difference.
 *
 * Mounted once, app-wide, rather than on a screen: the user is on *their* page
 * when they capture, so whatever this tab happens to be showing is not the
 * point — it only has to be open and signed in.
 */
export function useExtensionCaptures(): void {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  /**
   * The session each origin's captures go into, for as long as this tab is open.
   *
   * Without it every screenshot would be its own one-shot session — a dashboard of
   * "Session 1 … Session 9" for what was one pass over a page. A tab is the closest
   * thing the extension has to a session boundary, so it is the one used;
   * sessionStorage rather than a ref so a reload mid-capture-streak rejoins the
   * same session instead of quietly starting a second.
   */
  const startingRef = useRef(new Map<string, Promise<string>>());

  /**
   * Captures are recorded one at a time, in the order they were taken.
   *
   * The server places a new capture to the right of everything already on the
   * board, which it works out by *reading* the board — so two uploads in flight
   * at once both read the same board and both land at x: 0, stacked on top of
   * each other. Pressing the shortcut on two screens in a row does exactly
   * that, which is why this path serializes and `/browse` (one capture at a
   * time, by hand) never noticed.
   *
   * Only the write is serialized in effect; the queue is per tab, and the
   * upload of the next capture is not worth overlapping when the whole point
   * is that they land in a sensible row.
   */
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());

  const handle = useCallback(
    async (requestId: string, capture: ExtensionCapturePayload) => {
      let target = await resolveSession(capture.pageUrl, startingRef.current);

      const storagePath = await uploadCaptureImage(capture.image);
      const record = () =>
        createCapture({
          device: capture.device,
          dpr: capture.dpr,
          sessionId: target.sessionId,
          fullPage: false,
          height: capture.height,
          pageUrl: capture.pageUrl,
          storagePath,
          width: capture.width,
        });

      let row;
      try {
        row = await record();
      } catch (err) {
        // The remembered session is gone — deleted from another tab, or belonging
        // to an account this tab is no longer signed in as. Forgetting it and
        // starting a fresh one costs one round trip; not doing so makes every
        // capture of this origin fail until the tab is closed.
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
        forgetExtensionSession(capture.pageUrl);
        target = await resolveSession(capture.pageUrl, startingRef.current);
        row = await record();
      }
      // After both paths, so a capture saved on the retry is counted too.
      trackEvent('capture_taken', {
        device: capture.device,
        source: 'extension',
      });

      await queryClient.invalidateQueries({
        queryKey: canvasKeys.sessionCaptures(row.sessionId),
      });
      void queryClient.invalidateQueries({ queryKey: canvasKeys.projects() });
      void queryClient.invalidateQueries({ queryKey: canvasKeys.rooms() });
      void queryClient.invalidateQueries({
        queryKey: canvasKeys.projectCaptures(row.projectId),
      });

      // Whichever screen this tab is showing now knows a capture landed — and
      // which board it landed on, so the notice can offer to open it.
      useUiStore.getState().captureLanded({
        projectId: row.projectId,
        captureId: row.id,
        sessionId: row.sessionId,
      });

      replySuccess(requestId, {
        projectId: row.projectId,
        captureId: row.id,
        sessionId: row.sessionId,
      });
    },
    [queryClient],
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      // Only this page's own scripts — the extension's relay posts from here.
      if (event.source !== window) return;
      if (!isExtensionBridgeMessage(event.data)) return;

      // The extension answering the `ready` below. This is the whole install
      // detection: no extension id, no `externally_connectable`.
      if (event.data.kind === 'installed') {
        useUiStore.getState().extensionAnnounced(event.data.extensionVersion);
        trackEvent('extension_detected', {
          version: event.data.extensionVersion,
        });
        return;
      }

      if (event.data.kind !== 'capture') return;

      const { capture, requestId } = event.data;

      if (!user) {
        trackEvent('capture_failed', {
          reason: 'signed-out',
          source: 'extension',
        });
        replyFailure(requestId, 'signed-out');
        return;
      }

      // Announced before the upload starts, not after it finishes: the whole
      // gap this covers is the ten seconds where the row does not exist yet and
      // every list in the app therefore says there is nothing here.
      useUiStore.getState().captureIncoming();

      // Behind the queue, so a burst of captures lands in a row on the board
      // rather than in a pile. A failure must not break the chain for the ones
      // behind it, hence the catch inside.
      queueRef.current = queueRef.current.then(() =>
        handle(requestId, capture).catch((err: unknown) => {
          log.error({ err }, 'extension capture failed');
          useUiStore.getState().captureFailed();
          const [failure, message] = classify(err);
          trackEvent('capture_failed', {
            reason: failure,
            source: 'extension',
          });
          replyFailure(requestId, failure, message);
        }),
      );
    };

    window.addEventListener('message', onMessage);

    // Said on every mount, and again once the session resolves: the extension's
    // relay connects to its service worker only after hearing this, so a tab
    // that never announces is a tab it will never deliver to.
    announceReady();

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [handle, user]);
}

/**
 * Turns whatever failed into something the extension can say out loud.
 *
 * Everything 4xx used to come back as "Floto cannot make a board for this URL",
 * which is true of a 400 from the session route and of nothing else: an expired
 * session (401) and a bucket refusing the upload (403 — usually a missing CORS
 * rule for the origin this tab is served from) both produced a message that
 * sent the reader off to inspect a perfectly good URL. The API's own message
 * rides along, because three failure kinds cannot describe every 4xx.
 */
export function classify(
  err: unknown,
): [ExtensionCaptureFailure, string | undefined] {
  if (!(err instanceof ApiError)) {
    return ['upload-failed', err instanceof Error ? err.message : undefined];
  }

  // ApiError's message already carries the API's own words and the status.
  if (err.status === 401 || err.status === 403) {
    return ['signed-out', err.message];
  }
  if (err.status === 400 || err.status === 422) {
    return ['unusable-url', err.message];
  }
  return ['upload-failed', err.message];
}

interface SessionTarget {
  sessionId: string;
}

const SESSION_STORAGE_PREFIX = 'floto.extension.session.';
const PROJECT_STORAGE_PREFIX = 'floto.extension.project.';

function sessionKeyFor(pageUrl: string): string {
  return `${SESSION_STORAGE_PREFIX}${new URL(pageUrl).origin}`;
}

function projectKeyFor(pageUrl: string): string {
  return `${PROJECT_STORAGE_PREFIX}${new URL(pageUrl).origin}`;
}

/**
 * Drops a remembered session, so the next capture of this origin starts one.
 *
 * Exported because that is what "start a new session" means for the
 * extension: there is no session to create yet (the first capture creates
 * it), only the memory of the last one to forget. Pasting the URL again is
 * the gesture; this is the whole implementation of it.
 *
 * `projectId` is the project the next session should go in — the one the URL
 * was pasted from. Absent, the server makes a project named after the host.
 */
export function forgetExtensionSession(
  pageUrl: string,
  projectId?: null | string,
): void {
  sessionStorage.removeItem(sessionKeyFor(pageUrl));
  if (projectId) sessionStorage.setItem(projectKeyFor(pageUrl), projectId);
  else sessionStorage.removeItem(projectKeyFor(pageUrl));
}

/**
 * Points this origin's captures at an existing session — "continue in this
 * session" for a product the worker cannot open.
 */
export function joinExtensionSession(pageUrl: string, sessionId: string): void {
  sessionStorage.setItem(sessionKeyFor(pageUrl), sessionId);
}

/**
 * The session this origin's captures are going into, if one exists yet.
 *
 * Read by the screen rather than taken from the store's `landed`: that carries
 * no origin, so a capture of a different local port landing while this screen
 * is open would otherwise switch it to the wrong session.
 */
export function getExtensionSession(pageUrl: string): null | string {
  return sessionStorage.getItem(sessionKeyFor(pageUrl));
}

/**
 * The session this page's captures belong to, creating it on the first one.
 *
 * The in-flight promise is shared per origin, so a burst of captures — the
 * whole point of "stay on your page and keep pressing" — cannot race two
 * sessions into existence for the same page.
 */
async function resolveSession(
  pageUrl: string,
  starting: Map<string, Promise<string>>,
): Promise<SessionTarget> {
  const origin = new URL(pageUrl).origin;
  const key = sessionKeyFor(pageUrl);

  const stored = sessionStorage.getItem(key);
  if (stored) return { sessionId: stored };

  const existing = starting.get(origin);
  if (existing) return { sessionId: await existing };

  const projectId = sessionStorage.getItem(projectKeyFor(pageUrl));
  const pending = startSession(
    projectId ? { projectId, url: pageUrl } : { url: pageUrl },
  )
    .then((result) => {
      sessionStorage.setItem(key, result.session.id);
      return result.session.id;
    })
    .finally(() => {
      starting.delete(origin);
    });

  starting.set(origin, pending);

  return { sessionId: await pending };
}
